// Background refresh activity, app wide. A page that re-reads data already on screen (an event-driven
// call list reload, an analytics poll, the catch-up after a reconnect) reports it here instead of
// inserting an "Updating…" line into the page. The shell draws one thin bar along the bottom edge of
// the masthead (ui/RefreshBar.tsx) that takes no layout space, so nothing on the page moves.
//
// Ref counted: each begin() returns its own end(), and the bar stays while any refresh is open.
// Debounced: a refresh shorter than the show delay never draws the bar, and once drawn the bar stays
// for a minimum time so quick back-to-back refreshes don't flicker. A refresh still open after the slow
// threshold is the one thing worth telling a screen reader about.
import { useEffect, useSyncExternalStore } from 'react';

export const REFRESH_SHOW_DELAY_MS = 280;
export const REFRESH_MIN_VISIBLE_MS = 450;
export const REFRESH_SLOW_MS = 8000;

export interface RefreshActivitySnapshot {
  /** Refreshes in flight, shown or not. */
  readonly active: number;
  /** The bar is on screen. */
  readonly visible: boolean;
  /** A refresh has been open longer than the slow threshold. */
  readonly slow: boolean;
}

export interface RefreshActivityOptions {
  showDelayMs?: number;
  minVisibleMs?: number;
  slowMs?: number;
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

const IDLE: RefreshActivitySnapshot = Object.freeze({ active: 0, visible: false, slow: false });

export class RefreshActivity {
  readonly #showDelayMs: number;
  readonly #minVisibleMs: number;
  readonly #slowMs: number;
  readonly #now: () => number;
  readonly #setTimer: (fn: () => void, ms: number) => unknown;
  readonly #clearTimer: (handle: unknown) => void;
  readonly #listeners = new Set<() => void>();
  #snapshot: RefreshActivitySnapshot = IDLE;
  #count = 0;
  #visible = false;
  #slow = false;
  #shownAt = 0;
  #showTimer: unknown = null;
  #hideTimer: unknown = null;
  #slowTimer: unknown = null;

  constructor(options: RefreshActivityOptions = {}) {
    this.#showDelayMs = options.showDelayMs ?? REFRESH_SHOW_DELAY_MS;
    this.#minVisibleMs = options.minVisibleMs ?? REFRESH_MIN_VISIBLE_MS;
    this.#slowMs = options.slowMs ?? REFRESH_SLOW_MS;
    this.#now = options.now ?? (() => Date.now());
    this.#setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.#clearTimer = options.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>));
  }

  readonly getSnapshot = (): RefreshActivitySnapshot => this.#snapshot;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  /** Opens one refresh. The returned end() closes it; calling it again does nothing. */
  begin(): () => void {
    let open = true;
    this.#count += 1;
    if (this.#count === 1) this.#started();
    this.#publish();
    return () => {
      if (!open) return;
      open = false;
      this.#count -= 1;
      if (this.#count === 0) this.#settled();
      this.#publish();
    };
  }

  #started(): void {
    if (this.#hideTimer !== null) {
      // Still on screen, waiting out its minimum: keep it up rather than blink between refreshes.
      this.#clearTimer(this.#hideTimer);
      this.#hideTimer = null;
    } else if (!this.#visible && this.#showTimer === null) {
      this.#showTimer = this.#setTimer(() => {
        this.#showTimer = null;
        if (this.#count === 0) return;
        this.#visible = true;
        this.#shownAt = this.#now();
        this.#publish();
      }, this.#showDelayMs);
    }
    this.#slowTimer = this.#setTimer(() => {
      this.#slowTimer = null;
      if (this.#count === 0) return;
      this.#slow = true;
      this.#publish();
    }, this.#slowMs);
  }

  #settled(): void {
    if (this.#showTimer !== null) {
      this.#clearTimer(this.#showTimer);
      this.#showTimer = null;
    }
    if (this.#slowTimer !== null) {
      this.#clearTimer(this.#slowTimer);
      this.#slowTimer = null;
    }
    this.#slow = false;
    if (!this.#visible) return;
    const remaining = this.#minVisibleMs - (this.#now() - this.#shownAt);
    if (remaining <= 0) {
      this.#visible = false;
      return;
    }
    this.#hideTimer = this.#setTimer(() => {
      this.#hideTimer = null;
      if (this.#count > 0) return;
      this.#visible = false;
      this.#publish();
    }, remaining);
  }

  #publish(): void {
    const prev = this.#snapshot;
    if (prev.active === this.#count && prev.visible === this.#visible && prev.slow === this.#slow) return;
    this.#snapshot = this.#count === 0 && !this.#visible && !this.#slow ? IDLE : { active: this.#count, visible: this.#visible, slow: this.#slow };
    for (const listener of [...this.#listeners]) listener();
  }
}

/** The app's one store; the shell's bar reads it. */
export const refreshActivity = new RefreshActivity();

/** Holds a refresh open for as long as `active` is true and the component is mounted. */
export function useBackgroundRefresh(active: boolean, store: RefreshActivity = refreshActivity): void {
  useEffect(() => (active ? store.begin() : undefined), [active, store]);
}

export function useRefreshActivity(store: RefreshActivity = refreshActivity): RefreshActivitySnapshot {
  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}
